import { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAvatarUrl } from '../utils/avatar';
import { hasPermission } from '../utils/permission';
import { SocketContext } from '../context/SocketContextInstance';
import { useSocket } from '../hooks/useSocket';
import {
  LayoutDashboard, UserCircle, Users, UserPlus, ClipboardList,
  Activity, CreditCard, Banknote, PhoneCall, Key, ClipboardCheck,
  LogOut, Menu, X, Bell, Search, Hexagon, User, Stethoscope, Building2, Share2, Calendar, FileText,
  Inbox, HelpCircle, BarChart3, Settings, ChevronDown, ChevronRight,
  Clock, XCircle, CalendarClock, CheckCircle2, Plus, Tag, AlertCircle, Shield, DollarSign, Workflow,
  CalendarCheck, Webhook, ArrowDownLeft, ArrowUpRight, Quote, Globe, HeartHandshake, UserCheck
} from 'lucide-react';
import logo from '../assets/FinalLOGO.png';
import api from '../services/api';
import GlobalSearch from './GlobalSearch';
import { IncomingCallPopup } from './common/IncomingCallPopup';

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [avatarError, setAvatarError] = useState(false);
  const mainContentRef = useRef(null);

  useEffect(() => {
    setAvatarError(false);
  }, [user?.avatar]);

  const { notifications, unreadCount, markAllRead, markAsRead, incomingCalls = [], dismissIncomingCall = () => {} } = useSocket() || { notifications: [], unreadCount: 0, markAllRead: () => { }, markAsRead: () => { }, incomingCalls: [], dismissIncomingCall: () => {} };

  // ── RBAC: Call Permissions ──────────────────────────────────────────────────
  // Roles that can receive incoming call popups
  const CALL_RECEIVE_ROLES = ['admin', 'customer_care', 'cc_head', 'receptionist'];
  const canReceiveCalls = user && (
    CALL_RECEIVE_ROLES.includes(user.role) ||
    (Array.isArray(user.roleDetails?.permissions) &&
      user.roleDetails.permissions.some(p => {
        const name = typeof p === 'object' && p !== null ? p.name : p;
        return name === 'call:receive';
      }))
  );
  const [submittedOnboardingsCount, setSubmittedOnboardingsCount] = useState(0);

  // Instant response: Dismiss dot immediately upon navigating to onboarding review
  useEffect(() => {
    if (location.pathname === '/onboarding') {
      localStorage.setItem('lastOnboardingViewTime', new Date().toISOString());
      setSubmittedOnboardingsCount(0);
    }
  }, [location.pathname]);

  const [leadCounts, setLeadCounts] = useState({ pending: 0, followup: 0, followupToday: 0, lost: 0, completed: 0, lostSurgery: 0 });
  const [surgeryCounts, setSurgeryCounts] = useState({ active: 0, lost: 0 });

  useEffect(() => {
    if (user && ['admin', 'customer_care', 'cc_head', 'doctor', 'hospital'].includes(user.role)) {
      const fetchCounts = async () => {
        try {
          const isStaff = user && (user.role === 'admin' || ['customer_care', 'cc_head', 'accountant'].includes(user.role));
          const isClinical = ['admin', 'customer_care', 'cc_head', 'doctor', 'hospital'].includes(user.role);

          const promises = [];

          // Fetch onboarding count (staff only)
          if (isStaff) {
            promises.push(
              api.get('/onboarding/pending').then(res => {
                if (res.data?.success && Array.isArray(res.data?.data)) {
                  const lastViewTimeStr = localStorage.getItem('lastOnboardingViewTime');
                  if (location.pathname === '/onboarding') {
                    setSubmittedOnboardingsCount(0);
                  } else if (lastViewTimeStr) {
                    const lastViewTime = new Date(lastViewTimeStr);
                    const count = res.data.data.filter(c =>
                      c.onboardingStatus === 'submitted' &&
                      new Date(c.updatedAt || c.createdAt) > lastViewTime
                    ).length;
                    setSubmittedOnboardingsCount(count);
                  } else {
                    const count = res.data.data.filter(c => c.onboardingStatus === 'submitted').length;
                    setSubmittedOnboardingsCount(count);
                  }
                }
              }).catch(err => console.error('Failed to fetch onboarding count:', err))
            );

            // Fetch lead counts (staff only)
            promises.push(
              Promise.all([
                api.get('/leads?registrationPending=true&limit=1').catch(() => ({ data: null })),
                api.get('/leads?hasFollowUp=true&limit=1').catch(() => ({ data: null })),
                api.get('/leads?hasFollowUp=true&followUpToday=true&limit=1').catch(() => ({ data: null })),
                api.get('/leads?status=not_interested&limit=1').catch(() => ({ data: null })),
                api.get('/surgeries?status=closed&limit=1').catch(() => ({ data: null })),
                api.get('/surgeries?status=lost&limit=1').catch(() => ({ data: null }))
              ]).then(([pendingRes, followupRes, followupTodayRes, lostRes, completedSurgeriesRes, lostSurgeriesRes]) => {
                setLeadCounts({
                  pending: pendingRes.data?.data?.total || 0,
                  followup: followupRes.data?.data?.total || 0,
                  followupToday: followupTodayRes.data?.data?.total || 0,
                  lost: lostRes.data?.data?.total || 0,
                  completed: completedSurgeriesRes.data?.data?.pagination?.total || 0,
                  lostSurgery: lostSurgeriesRes.data?.data?.pagination?.total || 0
                });
              }).catch(err => console.error('Failed to fetch lead counts:', err))
            );
          }

          // Fetch surgery counts (staff + clinical/hospital)
          if (isClinical) {
            promises.push(
              Promise.all([
                api.get('/surgeries?status=active&limit=1').catch(() => ({ data: null })),
                api.get('/surgeries?status=lost&limit=1').catch(() => ({ data: null }))
              ]).then(([activeRes, lostRes]) => {
                setSurgeryCounts({
                  active: activeRes.data?.data?.pagination?.total || 0,
                  lost: lostRes.data?.data?.pagination?.total || 0
                });
              }).catch(err => console.error('Failed to fetch surgery counts:', err))
            );
          }

          await Promise.all(promises);
        } catch (error) {
          console.error('Failed to fetch count details:', error);
        }
      };

      fetchCounts();

      // Tab Visibility API: pause polling when user is on another tab to eliminate ghost server hits
      let intervalId = null;

      const startPolling = () => {
        if (!intervalId) {
          intervalId = setInterval(() => {
            if (document.visibilityState === 'visible') {
              fetchCounts();
            }
          }, 30000); // Check every 30 seconds
        }
      };

      const stopPolling = () => {
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      };

      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          fetchCounts();
          startPolling();
        } else {
          stopPolling();
        }
      };

      startPolling();
      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        stopPolling();
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [user?.role, location.pathname]);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});

  const checkIsSubActive = (subItem) => {
    const currentPath = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    const fromSource = searchParams.get('from') || searchParams.get('origin');
    const currentMenu = searchParams.get('menu');

    const isLeadDetailPath = /^\/leads\/[^/]+$/.test(currentPath) && !['reports', 'performance-reports', 'create', 'register-cc'].includes(currentPath.split('/')[2]);
    const isSurgeryDetailPath = /^\/surgeries\/[^/]+$/.test(currentPath);

    // 🎯 ORIGIN OVERRIDE: Maintain Active Cases sidebar highlight when viewing 1-14 workflow
    if (fromSource === 'surgeries' || currentMenu === 'surgeries') {
      if (isLeadDetailPath || isSurgeryDetailPath) {
        const detailTab = searchParams.get('tab') || 'active';
        const subItemParams = new URLSearchParams(subItem.path.split('?')[1] || '');
        const subItemTab = subItemParams.get('tab') || 'active';

        if (subItem.path.startsWith('/surgeries')) {
          return subItemTab === detailTab;
        }
        return false;
      }
    }

    // 🎯 ORIGIN OVERRIDE: Maintain single Lead Management tab sidebar highlight when viewing Surgery Details
    if (fromSource === 'leads' || currentMenu === 'leads') {
      if (isLeadDetailPath || isSurgeryDetailPath) {
        const detailTab = searchParams.get('tab') || 'pipeline';
        const subItemParams = new URLSearchParams(subItem.path.split('?')[1] || '');
        const subItemTab = subItemParams.get('tab') || 'pipeline';

        if (subItem.path.startsWith('/leads') && !['reports', 'performance-reports'].includes(subItem.path.split('/')[2])) {
          return subItemTab === detailTab;
        }
        return false;
      }
    }

    if (subItem.path.startsWith('/leads')) {
      const currentTab = searchParams.get('tab') || 'pipeline';
      const subItemParams = new URLSearchParams(subItem.path.split('?')[1] || '');
      const subItemTab = subItemParams.get('tab') || 'pipeline';

      const isBaseLeadsPath = currentPath === '/leads';
      if (subItem.path.startsWith('/leads/reports')) {
        return currentPath === '/leads/reports';
      }
      if (subItem.path.startsWith('/leads/performance-reports')) {
        return currentPath === '/leads/performance-reports';
      }

      if (isLeadDetailPath) {
        return subItemTab === 'pipeline';
      }

      return isBaseLeadsPath && currentTab === subItemTab;
    }
    if (subItem.path.startsWith('/surgeries')) {
      const currentTab = searchParams.get('tab') || 'active';
      const subItemParams = new URLSearchParams(subItem.path.split('?')[1] || '');
      const subItemTab = subItemParams.get('tab') || 'active';

      const isBaseSurgeriesPath = currentPath === '/surgeries';
      if (isSurgeryDetailPath && subItemTab === 'active') {
        return true;
      }
      return isBaseSurgeriesPath && currentTab === subItemTab;
    }
    if (subItem.path.startsWith('/appointments')) {
      const currentTab = searchParams.get('tab') || '';
      const subItemParams = new URLSearchParams(subItem.path.split('?')[1] || '');
      const subItemTab = subItemParams.get('tab') || '';

      const isBaseAppointmentsPath = currentPath === '/appointments';
      const isAppointmentCreatePath = currentPath === '/appointments/create' || currentPath.startsWith('/appointments/create');
      const isAppointmentFollowUpsPath = currentPath === '/appointments/follow-ups' || currentPath.startsWith('/appointments/follow-ups');
      const isAppointmentDetailPath = /^\/appointments\/[^/]+$/.test(currentPath) && !isAppointmentCreatePath && !isAppointmentFollowUpsPath;

      if (subItem.path === '/appointments/follow-ups') {
        return isAppointmentFollowUpsPath;
      }
      if (subItem.path === '/appointments/create') {
        return isAppointmentCreatePath;
      }
      if (subItem.path === '/appointments') {
        return (isBaseAppointmentsPath && currentTab === subItemTab) || isAppointmentDetailPath || isAppointmentCreatePath;
      }
      return isBaseAppointmentsPath && currentTab === subItemTab;
    }
    if (subItem.path === '/onboarding') {
      return currentPath === '/onboarding' || (currentPath.startsWith('/onboarding/') && !currentPath.startsWith('/onboarding/generate'));
    }
    if (subItem.path === '/payouts') {
      return currentPath === '/payouts' || (currentPath.startsWith('/payouts/') && !currentPath.startsWith('/payouts/action-center'));
    }
    if (subItem.path === '/payouts/action-center') {
      return currentPath === '/payouts/action-center';
    }
    if (subItem.path.startsWith('/webhooks')) {
      const currentTab = searchParams.get('tab') || 'inbound';
      const subItemParams = new URLSearchParams(subItem.path.split('?')[1] || '');
      const subItemTab = subItemParams.get('tab') || 'inbound';
      return currentPath === '/webhooks' && currentTab === subItemTab;
    }
    if (subItem.path === '/ophe-services') {
      return currentPath === '/ophe-services';
    }
    if (subItem.path.startsWith('/ophe/') || subItem.path === '/ophe') {
      const currentTab = searchParams.get('tab') || 'all';
      const subItemParams = new URLSearchParams(subItem.path.split('?')[1] || '');
      const subItemTab = subItemParams.get('tab') || 'all';

      if (currentPath.startsWith('/ophe/leads/')) {
        return subItemTab === 'all';
      }
      return (currentPath === '/ophe/pipeline' || currentPath === '/ophe/leads' || currentPath === '/ophe') && currentTab === subItemTab;
    }

    return currentPath === subItem.path || currentPath.startsWith(subItem.path + '/');
  };

  const toggleMenu = (label, currentExpanded) => {
    setExpandedMenus(prev => ({
      ...prev,
      [label]: !currentExpanded
    }));
  };

  const notificationRef = useRef(null);
  const userMenuRef = useRef(null);

  // Click Outside to Close Logic
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close notifications if clicked outside
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setNotificationsOpen(false);
      }
      // Close user menu if clicked outside
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    if (notificationsOpen || userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [notificationsOpen, userMenuOpen]);

  // Auto-close sidebar, expanded submenus and reset scroll on navigation
  useEffect(() => {
    setExpandedMenus({});
    if (window.innerWidth < 1280) {
      setSidebarOpen(false);
    }
    if (mainContentRef.current) {
      mainContentRef.current.scrollTo(0, 0);
    }
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getNavItems = () => {
    if (!user) return [];

    const items = [
      { path: '/dashboard', label: 'Dashboard', icon: <LayoutDashboard size={18} /> },
    ];

    const isAdmin = user.role === 'admin';
    const isSystemRole = ['customer_care', 'cc_head', 'accountant', 'doctor', 'hospital', 'marketing_agent'].includes(user.role);
    const hasCustomRole = !!user.roleDetails;
    const isStaffOrCustomRole = isAdmin || isSystemRole || hasCustomRole;

    // Helper: sidebar visibility is strictly controlled by explicit sidebar:* permissions
    // with a fallback to the module's functional actions permissions.
    const checkPermission = (perm) => {
      // Assuming hasPermission is imported, but it's not. Let's just define a local wrapper that checks user.
      if (!user) return false;
      if (user.role === 'admin') return true;
      if (Array.isArray(user.permissions) && (user.permissions.includes(perm) || user.permissions.includes('*'))) return true;
      if (user.roleDetails && typeof user.roleDetails === 'object') {
        const customPerms = user.roleDetails?.permissions;
        if (Array.isArray(customPerms) && customPerms.some(p => (typeof p === 'object' ? p.name === perm || p.name === '*' : p === perm || p === '*'))) return true;
      }
      return false;
    };

    const canShowSidebar = (sidebarPerm, modulePermFallbacks = []) => {
      if (isAdmin) return true;
      if (checkPermission(sidebarPerm)) return true;
      if (modulePermFallbacks.length > 0) {
        return modulePermFallbacks.some(p => checkPermission(p));
      }
      return false;
    };

    const canViewLeads = canShowSidebar('sidebar:lead_management', ['lead:read_all', 'lead:read_assigned', 'lead:create']);
    const canViewAppointments = canShowSidebar('sidebar:appointments', ['appointment:read', 'appointment:create']);
    const canViewSurgeries = canShowSidebar('sidebar:surgical_pipeline', ['surgery:view']);
    const canViewPayouts = canShowSidebar('sidebar:disbursals', ['payout:view_all', 'payout:view_own', 'payout:approve', 'payout:process']);
    const canViewReports = canShowSidebar('sidebar:reports', ['report:view']);
    const canViewPerfReports = canShowSidebar('sidebar:performance_reports', ['report:view']);
    const canViewOnboarding = canShowSidebar('sidebar:onboarding', []);
    const canViewRoster = canShowSidebar('sidebar:roster', []);
    const canViewDoctors = canShowSidebar('sidebar:doctors', ['user:view', 'user:create']);
    const canViewHospitals = canShowSidebar('sidebar:hospitals', ['user:view', 'user:create']) || ['admin', 'customer_care', 'cc_head', 'accountant'].includes(user.role);
    const canViewLeadPartners = canShowSidebar('sidebar:lead_partners', ['user:view', 'user:create']);
    const canViewSystemUsers = isAdmin || canShowSidebar('sidebar:system_users', []);
    const canViewRoles = isAdmin || canShowSidebar('sidebar:roles', ['role:manage']);
    const canViewActivityLogs = canShowSidebar('sidebar:activity_logs', ['report:view']);
    const canViewSupport = isAdmin || canShowSidebar('sidebar:support', []);
    const canViewSettings = isAdmin || canShowSidebar('sidebar:settings', []);
    const canViewMou = isAdmin || ['customer_care', 'cc_head'].includes(user.role) || canShowSidebar('sidebar:mou', ['mou:view', 'mou:manage']);
    const canViewWebhooks = isAdmin || canShowSidebar('sidebar:webhooks', ['webhook:view', 'webhook:manage']);
    const canViewCallAnalytics = canShowSidebar('sidebar:call_analytics', ['call_history:view_all', 'call_history:view_own']);
    const canViewOpheServices = isAdmin || ['customer_care', 'cc_head'].includes(user.role) || canShowSidebar('sidebar:ophe_services', ['ophe:view']);
    const canViewOphePipeline = isAdmin || ['customer_care', 'cc_head'].includes(user.role) || canShowSidebar('sidebar:ophe_pipeline', ['ophe:view', 'ophe:create']);

    // 1. Lead Management Module
    if (canViewLeads) {
      items.push({
        label: 'Lead Management',
        icon: <ClipboardList size={18} />,
        subItems: [
          { path: '/leads?tab=pipeline&menu=leads', label: 'All Leads', icon: <ClipboardList size={18} /> },
          { path: '/leads?tab=pending&menu=leads', label: 'New Leads', icon: <Clock size={18} /> },
          { path: '/leads?tab=followup&menu=leads', label: 'Follow-up Leads', icon: <CalendarClock size={18} /> },
          { path: '/leads?tab=lost&menu=leads', label: 'Lost Leads', icon: <XCircle size={18} /> },
        ]
      });
    }

    // 2. Appointment Pipeline
    if (canViewAppointments) {
      const apptSubItems = [];
      if (['doctor', 'hospital', 'marketing_agent'].includes(user.role)) {
        apptSubItems.push({ path: '/schedule-appointment', label: 'Schedule an Appointment', icon: <CalendarClock size={18} /> });
        if (user.role === 'doctor') {
          apptSubItems.push({ path: '/appointments', label: 'My Appointments', icon: <Calendar size={18} /> });
        }
      } else {
        apptSubItems.push({ path: '/appointments', label: 'All Appointments', icon: <Calendar size={18} /> });
      }
      apptSubItems.push({ path: '/appointments/follow-ups', label: 'Appointment Follow-ups', icon: <CalendarCheck size={18} /> });
      if (isAdmin || ['customer_care', 'cc_head'].includes(user.role)) {
        apptSubItems.push({ path: '/coupons', label: 'Coupons', icon: <Tag size={18} /> });
      }
      items.push({
        label: 'Appointments',
        icon: <Calendar size={18} />,
        subItems: apptSubItems
      });
    }

    // 3. Surgical Pipeline
    if (canViewSurgeries) {
      items.push({
        label: 'Surgical Pipeline',
        icon: <Activity size={18} />,
        subItems: [
          { path: '/surgeries?tab=active', label: 'Active Cases', icon: <Activity size={18} /> },
          { path: '/surgeries?tab=lost', label: 'Lost Cases', icon: <XCircle size={18} /> },
        ]
      });
    }

    // 3.5 OPHE Pipeline (Isolated Home Care & Elderly Care Operations)
    if (canViewOphePipeline || canViewOpheServices) {
      items.push({
        label: 'OPHE Pipeline',
        icon: <HeartHandshake size={18} />,
        subItems: [
          { path: '/ophe/pipeline?tab=all', label: 'OPHE Pipeline', icon: <HeartHandshake size={18} /> },
          { path: '/ophe/pipeline?tab=lost', label: 'Lost Leads', icon: <XCircle size={18} /> },
          { path: '/ophe-services', label: 'Services Catalog', icon: <Stethoscope size={18} /> },
        ]
      });
    }

    // 3.8 Call Analytics Module
    if (canViewCallAnalytics) {
      items.push({
        label: 'Call Analytics',
        icon: <PhoneCall size={18} />,
        subItems: [
          { path: '/call-analytics', label: 'Call History', icon: <PhoneCall size={18} /> },
        ]
      });
    }

    // 4. Reports & Performance
    if (canViewReports) {
      items.push({ path: '/leads/reports', label: 'Reports', icon: <FileText size={18} /> });
    }
    if (canViewPerfReports) {
      items.push({ path: '/leads/performance-reports', label: 'Performance Reports', icon: <BarChart3 size={18} /> });
    }

    // 5. Onboarding (Review / Website Requests / Invite)
    if (canViewOnboarding) {
      items.push({
        label: 'Onboarding',
        icon: <UserPlus size={18} />,
        subItems: [
          { path: '/onboarding', label: isAdmin ? 'Review Applications' : 'Review Partners', icon: <ClipboardCheck size={18} /> },
          { path: '/onboarding-requests', label: 'Website Requests', icon: <Inbox size={18} /> },
          { path: '/onboarding/generate', label: 'Invite Partners', icon: <UserPlus size={18} /> },
        ]
      });
    }

    // 5.5 Website Testimonials
    if (isAdmin || ['customer_care', 'cc_head'].includes(user.role)) {
      items.push({
        path: '/testimonials',
        label: 'Website Testimonials',
        icon: <Quote size={18} />
      });
    }

    // 6. Roster / Doctor Availability / OTP
    if (['doctor', 'hospital'].includes(user.role)) {
      items.push({ path: '/active-verifications', label: 'OTP Verifications', icon: <Key size={18} /> });
      if (user.role === 'doctor') {
        items.push({ path: '/doctor-availability', label: 'Availability', icon: <Calendar size={18} /> });
      }
    } else if (canViewRoster) {
      items.push({ path: '/doctor-availability/forecast', label: 'Roster Management', icon: <Calendar size={18} /> });
      items.push({ path: '/follow-ups', label: 'Follow-ups', icon: <PhoneCall size={18} /> });
      items.push({ path: '/otp', label: 'OTP Center', icon: <Key size={18} /> });
      items.push({ path: '/catalog', label: 'Services', icon: <Stethoscope size={18} /> });
    }

    // 7. System Directory & Role Management
    if (canViewDoctors) {
      items.push({ path: '/doctors', label: 'Doctors', icon: <Stethoscope size={18} /> });
    }
    if (canViewHospitals) {
      items.push({ path: '/hospitals', label: 'Hospitals', icon: <Building2 size={18} /> });
      items.push({ path: '/hospitals/rate-master', label: 'Hospital Rate Master', icon: <FileText size={18} /> });
    }
    if (canViewLeadPartners) {
      items.push({ path: '/marketing-agents', label: 'Lead Partners', icon: <Share2 size={18} /> });
    }
    if (canViewSystemUsers) {
      items.push({ path: '/users', label: 'System Users', icon: <Users size={18} /> });
    }
    if (canViewRoles) {
      items.push({ path: '/roles', label: 'Role & Permissions', icon: <Shield size={18} /> });
    }

    // 8. Disbursals & Financial Payouts (Unified - No Duplicate)
    if (canViewPayouts) {
      items.push({
        label: 'Disbursals & Payouts',
        icon: <Banknote size={18} />,
        subItems: [
          { path: '/payouts/action-center', label: 'Payout Action Center', icon: <Banknote size={18} /> },
          { path: '/payouts', label: 'Payout Ledger', icon: <FileText size={18} /> },
          { path: '/payments', label: 'Audit Log & Payments', icon: <CreditCard size={18} /> },
        ]
      });
    }

    // 8.5 Webhooks & API Integrations Module (Inbound & Outbound)
    if (canViewWebhooks) {
      items.push({
        label: 'Webhooks & API',
        icon: <Webhook size={18} />,
        subItems: [
          { path: '/webhooks?tab=inbound', label: 'Inbound Webhooks (Leads)', icon: <ArrowDownLeft size={18} /> },
          { path: '/webhooks?tab=outbound', label: 'Outbound Webhooks (Events)', icon: <ArrowUpRight size={18} /> },
          { path: '/webhooks?tab=logs', label: 'Delivery & Audit Logs', icon: <Clock size={18} /> },
        ]
      });
    }

    // 9. Admin Settings / System Logs
    const settingsSubItems = [
      ...(isAdmin ? [{ path: '/payment-config', label: 'Fee Settings', icon: <CreditCard size={18} /> }] : []),
      ...(canViewMou ? [{ path: '/mou-config', label: 'MoU Settings', icon: <FileText size={18} /> }] : []),
      ...(canViewWebhooks ? [{ path: '/webhooks', label: 'Webhooks & API', icon: <Webhook size={18} /> }] : []),
      ...(isAdmin ? [{ path: '/system-logs', label: 'System Audit', icon: <Activity size={18} /> }] : []),
    ];

    if (settingsSubItems.length > 0) {
      items.push({
        label: 'Settings',
        icon: <Settings size={18} />,
        subItems: settingsSubItems
      });
    } else if (canViewActivityLogs) {
      items.push({ path: '/system-logs', label: 'Activity Logs', icon: <Activity size={18} /> });
    }

    if (canViewSupport) {
      items.push({ path: '/support', label: 'Support & Help', icon: <PhoneCall size={18} /> });
    }

    return items;
  };

  const navItems = getNavItems();
  const currentRouteName = navItems.reduce((acc, item) => {
    if (item.subItems) {
      const found = item.subItems.reduce((bestMatch, current) => {
        const basePath = current.path.split('?')[0];
        if (location.pathname === basePath || location.pathname.startsWith(basePath + '/')) {
          if (current.path.includes('?')) {
            const currentTab = new URLSearchParams(location.search).get('tab') || (basePath === '/leads' ? 'pipeline' : (basePath === '/webhooks' ? 'inbound' : 'active'));
            const subItemTab = new URLSearchParams(current.path.split('?')[1]).get('tab');
            const currentMenu = new URLSearchParams(location.search).get('menu') || (basePath === '/leads' ? 'leads' : '');
            const subItemMenu = new URLSearchParams(current.path.split('?')[1]).get('menu') || (basePath === '/leads' ? 'leads' : '');
            if (currentTab === subItemTab && (!currentMenu || currentMenu === subItemMenu)) {
              return current;
            }
          } else {
            if (!bestMatch || !bestMatch.path.includes('?')) {
              return current;
            }
          }
        }
        return bestMatch;
      }, null);
      if (found) return found.label;
    }
    if (location.pathname === item.path || location.pathname.startsWith(item.path + '/')) return item.label;
    return acc;
  }, 'Workspace');

  return (
    <>
    <div className="min-h-screen bg-[#0f172a] flex items-center justify-center p-0 sm:p-4 lg:p-6 font-sans selection:bg-blue-100 selection:text-blue-900">

      {/* OS Window Frame */}
      <div className="w-full max-w-[1600px] h-[100vh] sm:h-[92vh] bg-[#F8FAFC] sm:rounded-[32px] shadow-[0_0_100px_rgba(0,0,0,0.5)] overflow-hidden flex flex-col border border-white/10 relative">


        {/* Top Command Bar */}
        <header className="h-16 bg-white/80 backdrop-blur-md border-b border-gray-100 flex items-center justify-between px-4 sm:px-6 z-30 flex-shrink-0">
          <div className="flex items-center gap-3">
            {/* Mobile Hamburger */}
            <button
              onClick={() => {
                setSidebarOpen(!sidebarOpen);
                setUserMenuOpen(false);
                setNotificationsOpen(false);
              }}
              className="xl:hidden p-2 rounded-xl text-gray-500 hover:bg-gray-100 transition-all"
            >
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <div className="flex items-center gap-3">
              <Link to="/dashboard" className="flex items-center gap-3" onClick={() => {
                setSidebarOpen(false);
                setUserMenuOpen(false);
                setNotificationsOpen(false);
              }}>
                <img src={logo} alt="Doxez" className="h-8 sm:h-12 w-auto object-contain" />
              </Link>
            </div>
            <div className="hidden md:flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-full">
              <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></div>
              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none">System Live</span>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-4">
            <GlobalSearch navItems={navItems} />

            {/* Global Support Button */}
            <button
              onClick={() => {
                const pathParts = location.pathname.split('/');
                let supportUrl = '/support';
                // Extract Case/Booking ID if we are on a detail page
                if (pathParts[1] === 'surgeries' && pathParts[2] && pathParts[2] !== 'test-ui') {
                  supportUrl += `?caseId=${pathParts[2]}&type=surgery`;
                } else if (pathParts[1] === 'leads' && pathParts[2] && pathParts[2] !== 'create' && pathParts[2] !== 'register-cc') {
                  supportUrl += `?caseId=${pathParts[2]}&type=lead`;
                }
                navigate(supportUrl);
                setUserMenuOpen(false);
                setSidebarOpen(false);
                setNotificationsOpen(false);
              }}
              className="hidden sm:block p-2 text-gray-400 hover:text-blue-600 hover:bg-gray-50 rounded-xl transition-all relative"
              title="Support & Help"
            >
              <HelpCircle size={20} />
            </button>

            <div className="relative" ref={notificationRef}>
              <button
                onClick={() => {
                  setNotificationsOpen(!notificationsOpen);
                  setUserMenuOpen(false);
                  setSidebarOpen(false);
                }}
                className="p-1.5 sm:p-2 text-gray-400 hover:text-blue-600 transition-colors relative"
              >
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span className="absolute top-0 right-0 sm:top-1 sm:right-1 min-w-[16px] h-4 bg-red-500 border-[1.5px] border-white rounded-full flex items-center justify-center text-[8px] font-black text-white px-0.5 shadow-sm">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {notificationsOpen && (
                <div className="fixed sm:absolute right-4 sm:right-0 mt-3 w-[calc(100vw-2rem)] sm:w-80 bg-white rounded-[24px] shadow-2xl border border-gray-100 p-2 z-[60] animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="px-4 py-3 border-b border-gray-50 flex items-center justify-between">
                    <h3 className="text-sm font-black text-[#143E78]">Notifications</h3>
                    <button
                      onClick={() => {
                        markAllRead();
                        setNotificationsOpen(false);
                      }}
                      className="text-[10px] font-black text-blue-500 hover:text-blue-700 uppercase tracking-tighter"
                    >
                      Clear All
                    </button>
                  </div>
                  <div className="max-h-[60vh] sm:max-h-[400px] overflow-y-auto p-1 py-2">
                    {notifications.length === 0 ? (
                      <div className="py-8 text-center">
                        <Bell className="mx-auto w-8 h-8 text-gray-200 mb-2" />
                        <p className="text-xs font-bold text-gray-400">All clear!</p>
                      </div>
                    ) : (
                      notifications.map((notif) => (
                        <div
                          key={notif._id || notif.id}
                          onClick={() => {
                            if (!notif.read) markAsRead(notif._id || notif.id);
                            if (notif.entityType === 'lead') navigate(`/leads/${notif.entityId}`);
                            if (notif.entityType === 'surgery') navigate(`/surgeries/${notif.entityId}`);
                            if (notif.entityType === 'payment') navigate(`/payments/${notif.entityId}`);
                            if (notif.entityType === 'ticket') navigate(`/support?ticketId=${notif.entityId}`);
                            if (notif.entityType === 'appointment') navigate(`/appointments/${notif.entityId}`);
                            setNotificationsOpen(false);
                          }}
                          className={`p-2.5 sm:p-3 rounded-2xl mb-1 cursor-pointer transition-all hover:bg-blue-100/50 hover:shadow-sm ${notif.read ? 'opacity-60 grayscale-[0.3]' : 'bg-blue-50 border border-blue-200 shadow-sm'}`}
                        >
                          <div className="flex items-start gap-2 sm:gap-3">
                            <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0 ${notif.entityType === 'lead' ? 'bg-blue-100 text-blue-600' :
                              notif.entityType === 'ticket' ? 'bg-purple-100 text-purple-600' :
                                notif.entityType === 'appointment' ? 'bg-amber-100 text-amber-600' :
                                  'bg-emerald-100 text-emerald-600'
                              }`}>
                              {notif.entityType === 'lead' ? <ClipboardList size={12} className="sm:w-[14px] sm:h-[14px]" /> :
                                notif.entityType === 'ticket' ? <PhoneCall size={12} className="sm:w-[14px] sm:h-[14px]" /> :
                                  notif.entityType === 'appointment' ? <CalendarClock size={12} className="sm:w-[14px] sm:h-[14px]" /> :
                                    <Activity size={12} className="sm:w-[14px] sm:h-[14px]" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[10px] sm:text-xs font-black text-gray-900 truncate uppercase tracking-tighter">
                                {notif.action?.replace(/_/g, ' ')}
                              </p>
                              <p className="text-[9px] sm:text-[11px] font-bold text-gray-500 line-clamp-2 leading-tight sm:leading-relaxed">
                                {notif.description}
                              </p>
                              <p className="text-[8px] sm:text-[9px] font-black text-gray-400 mt-1 uppercase">
                                {new Date(notif.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="h-6 sm:h-8 w-[1px] bg-gray-100 mx-1 sm:mx-2"></div>

            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => {
                  setUserMenuOpen(!userMenuOpen);
                  setSidebarOpen(false);
                  setNotificationsOpen(false);
                }}
                className="flex items-center gap-2 sm:gap-3 p-1 pr-1 sm:pr-3 hover:bg-gray-50 rounded-full transition-all"
              >
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-tr from-blue-50 to-blue-100 border border-blue-200 flex items-center justify-center text-blue-700 font-black overflow-hidden flex-shrink-0">
                  {user?.avatar && !avatarError ? (
                    <img
                      src={getAvatarUrl(user.avatar)}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={() => setAvatarError(true)}
                    />
                  ) : (
                    user?.firstName?.charAt(0)
                  )}
                </div>
                <div className="hidden sm:block text-left">
                  <p className="text-xs font-black text-[#143E78] leading-none mb-1">{user?.firstName}</p>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">{user?.role === 'marketing_agent' ? 'Lead Partner' : user?.role?.replace('_', ' ')}</p>
                </div>
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 mt-3 w-56 bg-white rounded-[24px] shadow-2xl border border-gray-100 p-2 z-[60] animate-in fade-in slide-in-from-top-2 duration-200">
                  <Link to="/profile" className="flex items-center gap-3 px-4 py-3 text-sm font-bold text-gray-600 hover:bg-blue-50 hover:text-blue-700 rounded-2xl transition-all" onClick={() => setUserMenuOpen(false)}>
                    <User size={18} /> My Profile
                  </Link>
                  <Link
                    to={(() => {
                      const pathParts = location.pathname.split('/');
                      if (pathParts[1] === 'surgeries' && pathParts[2] && pathParts[2] !== 'test-ui') {
                        return `/support?caseId=${pathParts[2]}&type=surgery`;
                      } else if (pathParts[1] === 'leads' && pathParts[2] && pathParts[2] !== 'create' && pathParts[2] !== 'register-cc') {
                        return `/support?caseId=${pathParts[2]}&type=lead`;
                      }
                      return '/support';
                    })()}
                    className="flex items-center gap-3 px-4 py-3 text-sm font-bold text-gray-600 hover:bg-blue-50 hover:text-blue-700 rounded-2xl transition-all"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    <HelpCircle size={18} /> Support & Help
                  </Link>
                  <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 text-sm font-bold text-red-500 hover:bg-red-50 rounded-2xl transition-all">
                    <LogOut size={18} /> Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="flex flex-1 overflow-hidden relative">


          {/* Mobile sidebar overlay backdrop */}
          {sidebarOpen && (
            <div
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm z-40 xl:hidden"
              onClick={() => setSidebarOpen(false)}
            />
          )}

          {/* Glass Sidebar */}
          <aside className={`
            absolute xl:relative inset-y-0 left-0 z-50 xl:z-30
            w-72 bg-white flex flex-col border-r border-gray-100/50
            transition-transform duration-300 ease-in-out
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full xl:translate-x-0'}
            h-full
          `}>
            <div className="flex-1 overflow-y-auto px-4 py-6">
              <nav className="space-y-1">
                <div className="flex items-center gap-3 px-4 mb-6">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.4em]">Operations</p>
                  <div className="flex-1 h-[1px] bg-gray-100/50"></div>
                </div>
                {navItems.map((item) => {
                  if (item.subItems) {
                    const isAnyChildActive = item.subItems.some(sub => checkIsSubActive(sub));
                    const isExpanded = expandedMenus[item.label] !== undefined ? expandedMenus[item.label] : isAnyChildActive;

                    return (
                      <div key={item.label} className="mb-1">
                        <button
                          onClick={() => toggleMenu(item.label, isExpanded)}
                          className={`w-full group flex items-center justify-between px-4 py-3.5 rounded-2xl text-sm font-bold transition-all duration-300 ${isAnyChildActive ? 'bg-blue-50 text-[#143E78]' : 'text-gray-500 hover:bg-blue-50 hover:text-[#143E78]'}`}
                        >
                          <div className="flex items-center">
                            <span className={`mr-4 transition-transform group-hover:scale-110 ${isAnyChildActive ? 'text-blue-500' : 'text-gray-400'}`}>
                              {item.icon}
                            </span>
                            <span className="tracking-tight">{item.label}</span>
                          </div>
                          {isExpanded ? <ChevronDown size={16} className={isAnyChildActive ? 'text-blue-500' : 'text-gray-400'} /> : <ChevronRight size={16} className={isAnyChildActive ? 'text-blue-500' : 'text-gray-400'} />}
                        </button>

                        {isExpanded && (
                          <div className="mt-1 ml-4 pl-4 border-l-2 border-gray-100 space-y-1 animate-in fade-in slide-in-from-top-2 duration-200">
                            {item.subItems.map(subItem => {
                              const isSubActive = checkIsSubActive(subItem);

                              return (
                                <Link
                                  key={subItem.path}
                                  to={subItem.path}
                                  onClick={() => { if (window.innerWidth < 1280) setSidebarOpen(false); }}
                                  className={`group flex items-center px-4 py-2.5 rounded-2xl text-sm font-bold transition-all duration-300 ${isSubActive
                                    ? 'bg-[#143E78] text-white shadow-md shadow-blue-900/20 translate-x-1'
                                    : 'text-gray-500 hover:bg-blue-50 hover:text-[#143E78] hover:translate-x-1'
                                    }`}
                                >
                                  <span className={`mr-3 transition-transform group-hover:scale-110 ${isSubActive ? 'text-blue-200' : 'text-gray-400'}`}>
                                    {subItem.icon}
                                  </span>
                                  <span className="tracking-tight flex-1">{subItem.label}</span>
                                  {subItem.label === 'New Leads' && leadCounts.pending > 0 && (
                                    <span className={`w-5 h-5 flex items-center justify-center text-[9px] font-black rounded-full min-w-[20px] px-1 ${isSubActive ? 'bg-white/20 text-white' : 'bg-amber-100 text-amber-700'}`}>
                                      {leadCounts.pending}
                                    </span>
                                  )}
                                  {subItem.label === 'Follow-up Leads' && (
                                    <div className="flex items-center gap-1.5">
                                      {leadCounts.followupToday > 0 && (
                                        <span className="relative flex h-2.5 w-2.5" title={`${leadCounts.followupToday} follow-up(s) due today`}>
                                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
                                          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-600"></span>
                                        </span>
                                      )}
                                      {leadCounts.followup > 0 && (
                                        <span className={`w-5 h-5 flex items-center justify-center text-[9px] font-black rounded-full min-w-[20px] px-1 transition-all ${leadCounts.followupToday > 0 ? (isSubActive ? 'bg-rose-500 text-white shadow-sm' : 'bg-rose-100 text-rose-700 font-black') : (isSubActive ? 'bg-white/20 text-white' : 'bg-indigo-100 text-indigo-700')}`}>
                                          {leadCounts.followup}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  {subItem.label === 'Lost Leads' && leadCounts.lost > 0 && (
                                    <span className={`w-5 h-5 flex items-center justify-center text-[9px] font-black rounded-full min-w-[20px] px-1 ${isSubActive ? 'bg-white/20 text-white' : 'bg-rose-100 text-rose-700'}`}>
                                      {leadCounts.lost}
                                    </span>
                                  )}
                                  {subItem.label === 'Complete to Surgery' && leadCounts.completed > 0 && (
                                    <span className={`w-5 h-5 flex items-center justify-center text-[9px] font-black rounded-full min-w-[20px] px-1 ${isSubActive ? 'bg-white/20 text-white' : 'bg-emerald-100 text-emerald-700'}`}>
                                      {leadCounts.completed}
                                    </span>
                                  )}
                                  {subItem.label === 'Lost Surgery' && leadCounts.lostSurgery > 0 && (
                                    <span className={`w-5 h-5 flex items-center justify-center text-[9px] font-black rounded-full min-w-[20px] px-1 ${isSubActive ? 'bg-white/20 text-white' : 'bg-rose-100 text-rose-700'}`}>
                                      {leadCounts.lostSurgery}
                                    </span>
                                  )}
                                  {subItem.label === 'Active Cases' && surgeryCounts.active > 0 && (
                                    <span className={`w-5 h-5 flex items-center justify-center text-[9px] font-black rounded-full min-w-[20px] px-1 ${isSubActive ? 'bg-white/20 text-white' : 'bg-blue-100 text-blue-700'}`}>
                                      {surgeryCounts.active}
                                    </span>
                                  )}
                                  {subItem.label === 'Lost Cases' && surgeryCounts.lost > 0 && (
                                    <span className={`w-5 h-5 flex items-center justify-center text-[9px] font-black rounded-full min-w-[20px] px-1 ${isSubActive ? 'bg-white/20 text-white' : 'bg-rose-100 text-rose-700'}`}>
                                      {surgeryCounts.lost}
                                    </span>
                                  )}
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  }

                  // Fix: Exclude specific sub-routes that have their own sidebar item from parent prefix match
                  const isActive = location.pathname === item.path || (
                    item.path !== '/dashboard' &&
                    item.path !== '/onboarding' &&
                    !(item.path === '/hospitals' && location.pathname.startsWith('/hospitals/rate-master')) &&
                    !(item.path === '/leads' && (location.pathname.startsWith('/leads/reports') || location.pathname.startsWith('/leads/performance-reports'))) &&
                    location.pathname.startsWith(item.path + '/')
                  );
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => { if (window.innerWidth < 1280) setSidebarOpen(false); }}
                      className={`group flex items-center px-4 py-3.5 rounded-2xl text-sm font-bold transition-all duration-300 ${isActive
                        ? 'bg-[#143E78] text-white shadow-xl shadow-blue-900/30 translate-x-2'
                        : 'text-gray-500 hover:bg-blue-50 hover:text-[#143E78] hover:translate-x-1'
                        }`}
                    >
                      <span className={`mr-4 transition-transform group-hover:scale-110 ${isActive ? 'text-blue-200' : 'text-gray-400'}`}>
                        {item.icon}
                      </span>
                      <span className="tracking-tight flex-1">{item.label}</span>
                      {item.path === '/onboarding' && submittedOnboardingsCount > 0 && (
                        <span className="relative flex h-2.5 w-2.5 mr-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500 shadow-md shadow-amber-200/50"></span>
                        </span>
                      )}
                    </Link>
                  );
                })}
              </nav>
            </div>

            {/* Sidebar Info Card */}
            <div className="p-4 border-t border-gray-50 space-y-3">


              {/* Quick Referral Link Button */}
              <Link
                to="/profile"
                onClick={() => { if (window.innerWidth < 1280) setSidebarOpen(false); }}
                className="flex items-center gap-3 bg-gradient-to-r from-[#143E78] to-[#1a5099] rounded-[18px] p-3.5 text-white hover:shadow-lg hover:shadow-blue-900/30 transition-all group/ref"
              >
                <div className="w-7 h-7 bg-white/15 rounded-xl flex items-center justify-center flex-shrink-0 group-hover/ref:bg-white/25 transition-all">
                  <Share2 size={14} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-black uppercase tracking-widest text-white/90">My Referral Link</p>
                  <p className="text-[9px] text-blue-200/60 font-bold">QR Code & Tracking</p>
                </div>
                <div className="w-1.5 h-1.5 bg-blue-300 rounded-full animate-pulse flex-shrink-0" />
              </Link>
            </div>
          </aside>

          {/* Main Stage */}
          <main ref={mainContentRef} className="flex-1 overflow-y-auto bg-[#F8FAFC] flex flex-col relative min-w-0">
            <div className="flex-shrink-0 px-4 sm:px-10 py-4 sm:py-8 flex items-center justify-between sticky top-0 bg-[#F8FAFC]/90 backdrop-blur-md z-20 border-b border-gray-100/50">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <div className="w-1 sm:w-1.5 h-4 sm:h-6 bg-blue-600 rounded-full"></div>
                  <h2 className="text-[10px] sm:text-[11px] font-black text-[#143E78] uppercase tracking-[0.2em] sm:tracking-[0.4em] leading-none">{currentRouteName}</h2>
                </div>
              </div>
            </div>
            <div className="px-4 sm:px-6 lg:px-8 pb-8 flex-1 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <Outlet />
            </div>
          </main>

        </div>
      </div>
    </div>

    {/* Exotel Incoming Call Popup — only for users with call:receive permission */}
    {canReceiveCalls && (
      <IncomingCallPopup
        incomingCalls={incomingCalls}
        onDismiss={dismissIncomingCall}
      />
    )}
    </>
  );
};

export default Layout;
